import React, { useCallback, useEffect, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { ArtifactDetail, ArtifactsSlider, CelestialRosesSidebar } from '@pkg/components';
import { useAppSelector } from '@pkg/hooks';

import { ArtifactType } from '@app/client/common/structs/nft/artifacts';
import { PathParams } from '@app/routes/types';
import { artifactCountsSelector } from '@app/store/selectors/artifacts/artifacts';
import { combinedRoseSelector, userCelestialRosesSelector } from '@app/store/selectors/artifacts/celestial-roses';

import './CelestialRosesList.scss';

type CelestialRosesListProps = {
    expanded: boolean;
    muted: boolean;
};

export function getCelestialRoseIndex(celestialRoses, id) {
    return celestialRoses.findIndex((celestialRose) => celestialRose?.id === id);
}

export function CelestialRosesList({ expanded, muted }: CelestialRosesListProps) {
    const navigate = useNavigate();
    const { [PathParams.Id]: urlArtifactId } = useParams();
    const artifactSupplyCounts = useAppSelector(artifactCountsSelector);
    const celestialRoseDisplayItems = useAppSelector(combinedRoseSelector);
    const userRoseItemMetadata = useAppSelector(userCelestialRosesSelector);
    const initialRoseIndex = getCelestialRoseIndex(celestialRoseDisplayItems, urlArtifactId);
    const [activeIndex, setActiveIndex] = useState(initialRoseIndex);

    const counts = artifactSupplyCounts[ArtifactType.CelestialRose];
    useEffect(() => {
        const newIndex = getCelestialRoseIndex(celestialRoseDisplayItems, urlArtifactId);
        setActiveIndex(newIndex);
    }, [celestialRoseDisplayItems, urlArtifactId]);

    const goToCelestialRoseAtIndex = useCallback(
        (index) => {
            const currentRose = celestialRoseDisplayItems[index];
            if (currentRose) {
                navigate(`/artifacts/celestial-rose/${currentRose.id}`);
            }
        },
        [navigate, celestialRoseDisplayItems],
    );

    const handleSlideChange = useCallback(
        (currentIndex) => {
            goToCelestialRoseAtIndex(currentIndex);
        },
        [goToCelestialRoseAtIndex],
    );

    if (!urlArtifactId) {
        return null;
    }

    return (
        <div className="yat-artifacts__celestial-roses-list">
            <CelestialRosesSidebar totalCount={counts?.total || 0} ownedCount={userRoseItemMetadata?.length} />

            <ArtifactsSlider expanded={expanded} activeIndex={activeIndex} onChange={handleSlideChange}>
                {celestialRoseDisplayItems.map((celestialRoseDisplayItem) => {
                    const { id, image, animation_url } = celestialRoseDisplayItem;
                    const isActive = urlArtifactId === id;

                    return (
                        <ArtifactDetail
                            key={id}
                            artifactType={ArtifactType.CelestialRose}
                            artifactId={id}
                            active={isActive}
                            expanded={expanded}
                            muted={muted}
                            videoUrl={animation_url}
                            posterUrl={image}
                        />
                    );
                })}
            </ArtifactsSlider>
        </div>
    );
}
